import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileCheck2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" }) + " HE" : "—");

export default function MesRapports() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/demandes/mes-rapports").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const shown = useMemo(() => (rows || []).filter((r) => `${r.numero} ${r.type_examen || ""}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const dl = (r) => downloadFile(`/demandes/${r.id}/compte-rendu/pdf`, `Rapport_${r.numero}.pdf`).catch(() => toast.error("Téléchargement impossible"));
  return (
    <div className="space-y-5" data-testid="mes-rapports-page">
      <PageHeader title="Mes rapports de pathologie signés" subtitle="Les rapports des demandes que vous avez envoyées, dès leur signature par le pathologiste." />
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input className="pl-9" placeholder="Rechercher un numéro ou un examen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mes-rapports-search" />
      </div>
      {rows === null ? <div className="text-sm text-slate-400">Chargement…</div>
        : shown.length === 0 ? (
          <Card className="p-8 text-center text-sm text-slate-500" data-testid="mes-rapports-empty">
            <FileCheck2 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            {rows.length ? "Aucun rapport ne correspond à la recherche." : "Aucun rapport signé pour l'instant. Vous recevrez un courriel dès qu'un rapport sera disponible."}
          </Card>
        ) : (
          <div className="grid gap-3" data-testid="mes-rapports-list">
            {shown.map((r) => (
              <Card key={r.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`mes-rapports-row-${r.numero}`}>
                <div className="flex-1 min-w-[220px]">
                  <Link to={`/demandes/${r.id}`} className="font-semibold text-slate-900 hover:text-sky-700">{r.numero}</Link>
                  <span className="ml-2 text-sm text-slate-600">{r.type_examen || ""}</span>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Signé le {fmt(r.signe_le)}{r.signataire_nom ? ` par ${r.signataire_nom}` : ""}
                    {r.addenda > 0 && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800" data-testid={`mes-rapports-addenda-${r.numero}`}>{r.addenda} addenda</span>}
                  </div>
                </div>
                <Button variant="outline" onClick={() => dl(r)} data-testid={`mes-rapports-pdf-${r.numero}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}
